import React, { useCallback, useEffect, useState } from 'react';
import { dmClient } from '../../services/dmClient';
import { useAuth } from '../../auth/AuthContext';
import './admin.css';

const ROLES = ['admin', 'analyst', 'viewer'];

export default function UsersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [invite, setInvite] = useState({ email: '', displayName: '', role: 'analyst' });
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await dmClient.get('/users');
      setItems(resp.data?.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setError('');
    setNotice('');
    try {
      const resp = await dmClient.post('/users', invite);
      setNotice(resp.data?.message || 'Invite sent.');
      setInvite({ email: '', displayName: '', role: 'analyst' });
      load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (u, role) => {
    setError('');
    try {
      await dmClient.patch(`/users/${u.UserId}`, { roles: [role] });
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to update role');
    }
  };

  const setStatus = async (u, status) => {
    setError('');
    try {
      if (status === 'disabled') {
        await dmClient.post(`/users/${u.UserId}/deactivate`, {});
      } else {
        await dmClient.patch(`/users/${u.UserId}`, { status });
      }
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to update status');
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(
      `Delete ${u.Email}? They have not set a password yet, so this removes them entirely and lets you send a fresh invite. This cannot be undone.`
    )) return;
    setError('');
    setNotice('');
    try {
      await dmClient.delete(`/users/${u.UserId}`);
      setNotice(`Deleted ${u.Email}. You can now re-invite this address.`);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to delete user');
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-head">
        <div>
          <h1>Users</h1>
          <p>Invite teammates and manage their roles for your organization.</p>
        </div>
      </div>

      {error && <div className="admin-alert admin-alert--error">{error}</div>}
      {notice && <div className="admin-alert admin-alert--ok">{notice}</div>}

      <form className="admin-card admin-invite" onSubmit={sendInvite}>
        <h3>Invite a user</h3>
        <div className="admin-invite__row">
          <input
            type="email" placeholder="teammate@company.com" required
            value={invite.email}
            onChange={(e) => setInvite((v) => ({ ...v, email: e.target.value }))}
          />
          <input
            type="text" placeholder="Full name (optional)"
            value={invite.displayName}
            onChange={(e) => setInvite((v) => ({ ...v, displayName: e.target.value }))}
          />
          <select
            value={invite.role}
            onChange={(e) => setInvite((v) => ({ ...v, role: e.target.value }))}
          >
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button type="submit" className="admin-btn admin-btn--primary" disabled={inviting}>
            {inviting ? 'Sending…' : 'Send invite'}
          </button>
        </div>
        <p className="admin-hint">
          The user receives an email link to set their password and join.
        </p>
      </form>

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last login</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="admin-empty">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="admin-empty">No users yet.</td></tr>
            )}
            {!loading && items.map((u) => {
              const isSelf = u.UserId === user?.userId;
              const role = (u.roles && u.roles[0]) || 'analyst';
              return (
                <tr key={u.UserId}>
                  <td>{u.Email}{isSelf && <span className="admin-tag">you</span>}</td>
                  <td>{u.DisplayName || '—'}</td>
                  <td>
                    <select value={role} disabled={isSelf}
                            onChange={(e) => changeRole(u, e.target.value)}>
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td>
                    <span className={`admin-status admin-status--${(u.Status || '').toLowerCase()}`}>
                      {u.Status}
                    </span>
                  </td>
                  <td>{u.LastLoginAt ? new Date(u.LastLoginAt).toLocaleString() : '—'}</td>
                  <td className="admin-row-actions">
                    {!isSelf && !u.HasPassword && (
                      <button className="admin-btn admin-btn--danger" onClick={() => deleteUser(u)}>
                        Delete
                      </button>
                    )}
                    {!isSelf && u.HasPassword && u.Status === 'active' && (
                      <button className="admin-btn admin-btn--danger" onClick={() => setStatus(u, 'disabled')}>
                        Deactivate
                      </button>
                    )}
                    {!isSelf && u.HasPassword && u.Status === 'disabled' && (
                      <button className="admin-btn" onClick={() => setStatus(u, 'active')}>
                        Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
